/**
 * WordPress dependencies
 */
import { useReducer, useRef } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import Button from '../Button/Button';
import TextInput from '../TextInput';
import PostcodeInput from '../PostcodeInput';
import RadioInput from '../RadioInput';
import CheckboxInput from '../CheckboxInput';
import Amounts from './Amounts';
import Vipps from '../icons/Vipps';
import handleSubmitForm from '../../utils/handleSubmitDonationForm';
import validateEmail from '../../utils/validateEmail';

function DonationForm({ formID }) {
	const formConfig = window.dekode_profundo_form[formID] ?? [];
	const formURL = formConfig['formURL'] ?? '';
	const appID = formConfig['appID'] ?? '';
	const formType = formConfig['formType'] ?? 'both';
	const formTitle = formConfig['formTitle'] ?? __('Choose how you want to contribute', 'dekode-profundo-forms');
	const stepTwoTitle = formConfig['stepTwoTitle'] ?? __('Tell us a little about yourself', 'dekode-profundo-forms');
	const stepTwoInfo = formConfig['stepTwoInfo'] ?? __('We use e-mail and mobile number to keep you up to date.', 'dekode-profundo-forms');
	const amountsSingle = formConfig['amountsSingle'] ?? [];
	const amountsRecurring = formConfig['amountsRecurring'] ?? [];
	const formInstance = formConfig['formInstance'] ?? 0;
	const recurringOrder = formConfig['recurringOrder'] ?? 'recurring';
	const requiredFields = formConfig['requiredFields'] ??  [];
	const commentFieldSingleLabel = formConfig['commentFieldSingleLabel'] ??  '';
	const commentFieldRecurringLabel = formConfig['commentFieldRecurringLabel'] ??  '';
	const disableVipps = formConfig['disableVipps'] ?? false;

	const [formData, setFormData] = useReducer(
		( s, a ) => ({ ...s, ...a }),
		{
			loading: false,
			step: 1,
			frequency: formConfig['defaultFrequency'] ?? 'recurring',
			amount: 'single' === formConfig['defaultFrequency'] ? amountsSingle[1] : amountsRecurring[1],
			custom_amount: '',
			anonymous: false,
			name: '',
			firstname: '',
			lastname: '',
			address: '',
			postcode: '',
			city: '',
			mobile: '',
			email: '',
			tax_deduction: false,
			personal_id: '',
			errors: {},
			message: '',
			commentSingle: '',
			commentRecurring: ''
		}
	);

	const {
		loading,
		step,
		frequency,
		amount,
		custom_amount,
		anonymous,
		name,
		address,
		postcode,
		city,
		mobile,
		email,
		tax_deduction,
		personal_id,
		message,
		errors,
		commentSingle,
		commentRecurring,
	} = formData;

	const nameRef = useRef('');

	const minimumAmount = 'single' === frequency ? 1 : 150;

	const validateForm = (payment_method = 'vipps') => {
		let fields = {};

		const addError = (field, message) => {
			if ( undefined === fields[field] ) {
				fields[field] = [];
			}

			fields[field].push(message);
		}

		if ( 'single' === frequency && false === anonymous ) {
			if ( minimumAmount > amount ) {
				// translators: Placeholder for the minimum donation amount.
				addError('amount', sprintf(__('The minimum donation amount is %s', 'dekode-profundo-forms'), minimumAmount));
			}
		} else {
			if ( minimumAmount > amount ) {
				// translators: Placeholder for the minimum donation amount.
				addError('amount', sprintf(__('The minimum donation amount is %s', 'dekode-profundo-forms'), minimumAmount));
			}
		}

		if ( 'vipps' !== payment_method && false === anonymous ) {
			// Default to require mobile and name if no fields are set to required and it's not an anonymous form.
			const defaultRequiredFields = [ 'mobile', 'name' ];
			const required = requiredFields.length ? requiredFields : defaultRequiredFields;

			// Add error if a required field is missing.
			required.forEach((field) => {
				if ( 0 === formData[field].length ) {
					let skip = false;
					
					// Skip requirement if single comment field for recurring donations, or recurring comment for single.
					if (
						('commentRecurring' === field && 'single' === frequency) ||
						('commentSingle' === field && 'recurring' === frequency)
					) {
						skip = true;
					}

					if (!skip) {
						addError(field, __('This field is required', 'dekode-profundo-forms'));
					}
				}
			});

			// Check for valid email address.
			if ( email.length > 0 ) {
				if ( !validateEmail(email) ) {
					addError('email', __('Please enter a valid email address', 'dekode-profundo-forms'));
				}
			}

			// Check for valid postcode.
			if ( postcode.length > 0 ) {
				if ( 4 !== postcode.length ) {
					addError('postcode', __('Please enter a valid postcode', 'dekode-profundo-forms'));
				}
			}
		}

		if (0 === Object.keys(fields).length) {
			setFormData({ errors: [] });
			submitForm(payment_method);
		} else {
			setFormData({ errors: fields });
		}
	}

	const submitForm = (payment_method = 'vipps') => {
		handleSubmitForm(payment_method, formURL, formData, appID, setFormData);
	}

	const generateName = (name) => {
		return name + formInstance;
	}

	const handleMobileChange = (mobile) => {
		setFormData({ mobile });

		if ( mobile.length >= 8 ) {
			fetch('https://gi.blakors.no/api/mobil?' + mobile)
			.then((response) => {
				return response.json();
			})
			.then((response) => {
				let newData = [];
				if ( response.navn && 0 === nameRef.current.length ) {
					newData['name'] = response.navn;
				}

				if ( response.adresse ) {
					newData['address'] = response.adresse;
				}

				if ( response.poststed ) {
					newData['city'] = response.poststed;
				}

				if ( response.postnummer ) {
					newData['postcode'] = response.postnummer;
				}

				setFormData({ ...newData });
			});
		}
	}

	const handleNameChange = (name) => {
		setFormData({ name });
		nameRef.current = name;
	}

	let formTypeEle = [
		<RadioInput
			key="frequency-recurring"
			checked={'recurring' === frequency}
			name="frequency"
			id={generateName('frequency-recurring')}
			label={__('Give monthly', 'dekode-profundo-forms')}
			onChange={() => setFormData({ frequency: 'recurring', amount: amountsRecurring[0], custom_amount: '' })}
			disabled={loading}
		/>,
		<RadioInput
			key="frequency-single"
			checked={'single' === frequency}
			name="frequency"
			id={generateName('frequency-single')}
			label={__('Give a single gift', 'dekode-profundo-forms')}
			onChange={() => setFormData({ frequency: 'single', amount: amountsSingle[0], custom_amount: '' })}
			disabled={loading}
		/>
	];
	
	if ( recurringOrder === 'single' ) {
		formTypeEle.reverse();
	}

	return (
		<form className="dpf_donation-form" noValidate>
			{ 1 === step &&
				<div className="dpf_step" id="step1">
					<p>{formTitle}</p>
					{ 'both' === formType &&
						<div className="dpf_frequencies">
							{formTypeEle}
						</div>
					}

					<Amounts
						amounts={'single' === frequency ? amountsSingle : amountsRecurring}
						setAmount={(value) => setFormData({ amount: value })}
						customAmount={custom_amount}
						setCustomAmount={(value) => setFormData({ custom_amount: value })}
						currentAmount={amount}
						minimumAmount={minimumAmount}
						frequency={frequency}
						disabled={loading}
						error={errors['amount'] ?? null}
					/>

					<div className="dpf_actions">
						{'single' === frequency ? (
							<>
								{!disableVipps && (
									<Button
										onClick={() => validateForm('vipps')}
										type="submit"
										className="dpf_button--vipps"
										customClass={'dpf_button--vipps' + (loading ? ' dpf_button--loading' : '')}
										disabled={loading}
									>
										{__('Give with', 'dekode-profundo-forms')}
										<span className="screen-reader"> {__('Vipps', 'dekode-profundo-forms')}</span>
										<Vipps />
									</Button>
								)}
								<Button
									customClass="dpf_button--card"
									onClick={() => setFormData({ step: 2 })}
									disabled={loading}
								>
									{__('Give with card', 'dekode-profundo-forms')}
								</Button>
							</>
						) : (
							<>
								{!disableVipps && (
									<Button
										onClick={() => validateForm('vipps')}
										type="submit"
										customClass={'dpf_button--vipps' + (loading ? ' dpf_button--loading' : '')}
										disabled={loading}
									>
										{__('Give monthly with', 'dekode-profundo-forms')}
										<span className="screen-reader"> {__('Vipps', 'dekode-profundo-forms')}</span>
										<Vipps />
									</Button>
								)}
								<Button
									customClass="dpf_button--direct-debit"
									onClick={() => setFormData({ step: 2 })}
									disabled={loading}
								>
									{__('Give by direct debit', 'dekode-profundo-forms')}
								</Button>
							</>
						)}
					</div>
				</div>
			}

			{ 2 === step &&
				<div className="dpf_step" id="step2">
					<div className="dpf_back">
						<Button
							onClick={() => setFormData({ step: 1 })}
							disabled={loading}
						>
							{__('Back', 'dekode-profundo-forms')}
						</Button>
					</div>
					<p>{stepTwoTitle}</p>
					<div className="dpf_fields">
						<TextInput
							name={generateName('mobile')}
							label={__('Mobile', 'dekode-profundo-forms')}
							onChange={handleMobileChange}
							value={mobile}
							required={(false === anonymous && 0 === requiredFields.length) || requiredFields.includes('mobile')}
							error={errors['mobile'] ?? null}
							disabled={loading}
						/>
						<TextInput
							name={generateName('fullname')}
							label={__('Full Name', 'dekode-profundo-forms')}
							onChange={handleNameChange}
							value={name}
							required={(false === anonymous && 0 === requiredFields.length) || requiredFields.includes('name')}
							error={errors['name'] ?? null}
							disabled={loading}
						/>
						<TextInput
							name={generateName('address')}
							label={__('Address', 'dekode-profundo-forms')}
							onChange={(address) => setFormData({ address })}
							value={address}
							required={requiredFields.includes('address')}
							error={errors['address'] ?? null}
							disabled={loading}
						/>
						<PostcodeInput
							name={generateName('postcode')}
							label={__('Postcode', 'dekode-profundo-forms')}
							onChange={(postcode) => setFormData({ postcode })}
							setCity={(city) => setFormData({ city })}
							value={postcode}
							required={requiredFields.includes('postcode')}
							error={errors['postcode'] ?? null}
							disabled={loading}
						/>
						<TextInput
							name={generateName('city')}
							label={__('City', 'dekode-profundo-forms')}
							onChange={(city) => setFormData({ city })}
							value={city}
							required={requiredFields.includes('city')}
							error={errors['city'] ?? null}
							disabled={loading}
						/>
						<TextInput
							name={generateName('email')}
							label={__('Email', 'dekode-profundo-forms')}
							onChange={(email) => setFormData({ email })}
							value={email}
							required={requiredFields.includes('email')}
							error={errors['email'] ?? null}
							disabled={loading}
						/>
						<p>{stepTwoInfo}</p>

						{commentFieldSingleLabel && ['single','both'].includes(frequency) &&
							<TextInput
								name={generateName('comment-single')}
								label={commentFieldSingleLabel}
								onChange={(commentSingle) => setFormData({ commentSingle })}
								value={commentSingle}
								required={requiredFields.includes('commentSingle')}
								error={errors['commentSingle'] ?? null}
								disabled={loading}
							/>
						}

						{commentFieldRecurringLabel && ['recurring','both'].includes(frequency) &&
							<TextInput
								name={generateName('comment-recurring')}
								label={commentFieldRecurringLabel}
								onChange={(commentRecurring) => setFormData({commentRecurring})}
								value={commentRecurring}
								required={requiredFields.includes('commentRecurring')}
								error={errors['commentRecurring'] ?? null}
								disabled={loading}
							/>
						}

						{ 'single' === frequency ? (
							!requiredFields.length && (
								<>
									<CheckboxInput
										name="anonymous"
										id={generateName('anonymous')}
										label={__('I want to be anonymous', 'dekode-profundo-forms')}
										onChange={() => setFormData({ anonymous: !anonymous })}
										checked={anonymous}
										disabled={loading}
									/>
								</>
							)
						) : (
							<>
								<CheckboxInput
									name="tax-deduction"
									id={generateName('tax-deduction')}
									label={__('I want a tax deduction', 'dekode-profundo-forms')}
									onChange={() => setFormData({ tax_deduction: tax_deduction ? false : true })}
									checked={tax_deduction}
									disabled={loading}
								/>

								{ tax_deduction &&
									<TextInput
										name={generateName('personal_id')}
										label={__('Social security number / Organization number', 'dekode-profundo-forms')}
										onChange={(personal_id) => setFormData({ personal_id })}
										value={personal_id}
										disabled={loading}
									/>
								}
							</>
						)}
					</div>
					<div className="dpf_actions">
						{ 'single' === frequency ? (
							<>
								<Button
									onClick={() => validateForm('nets')}
									type="submit"
									customClass={ loading ? 'dpf_button--loading' : null}
									disabled={loading}
								>
									{__('Give with card', 'dekode-profundo-forms')}
								</Button>
							</>
						) : (
							<>
								<Button
									onClick={() => validateForm('avtalegiro')}
									type="submit"
									customClass={ loading ? 'dpf_button--loading' : null}
									disabled={loading}
								>
									{__('Give by direct debit', 'dekode-profundo-forms')}
								</Button>
							</>
						)}
					</div>
				</div>
			}

			{ '' !== message &&
				<div className="dpf_message dpf_message--error">
					{message}
				</div>
			}
		</form>
	);
}

export default DonationForm;
